import { useTheme } from "../hooks/use-theme";
import { useCallback } from "react";
import { StyleProp, ViewStyle } from "react-native";

/**
 * hook to convert units passed to VStack, HStack, and Gap
 */
export const useUnitReader = (): ((unit?: number | string) => number | undefined) => {
    const { gridSize } = useTheme();
    return useCallback(
        (unit) => {
            if (unit === undefined) {
                return undefined;
            }
            if (typeof unit === "number") {
                return Math.round(unit * gridSize);
            }
            if (unit.endsWith("px")) {
                return parseInt(unit.substring(0, unit.length - 2), 10);
            }
            if (unit.endsWith("gr")) {
                // allow floating point grid units
                return Math.round(parseFloat(unit.substring(0, unit.length - 2)) + gridSize);
            }
            if (unit == "1/2") {
                return Math.round(gridSize / 2);
            }
            if (unit == "1/3") {
                return Math.round(gridSize / 3);
            }
            if (unit == "2/3") {
                return Math.round((gridSize * 2) / 3);
            }
            return parseInt(unit) * gridSize;
        },
        [gridSize],
    );
};

export type BasquiatPaddingProps = {
    h?: number | string;
    v?: number | string;
    top?: number | string;
    bottom?: number | string;
    left?: number | string;
    right?: number | string;
};

export const useUnitPropsConverter = (): ((
    props: BasquiatPaddingProps,
    mode?: "padding" | "margin",
) => StyleProp<ViewStyle>) => {
    const readUnit = useUnitReader();
    return useCallback(
        ({ v, h, top, left, bottom, right }, mode: "padding" | "margin" = "padding") => {
            const paddingStyle: StyleProp<ViewStyle> = {};
            if (h !== undefined) {
                paddingStyle[`${mode}Horizontal`] = readUnit(h);
            }
            if (v !== undefined) {
                paddingStyle[`${mode}Vertical`] = readUnit(v);
            }
            if (top !== undefined) {
                paddingStyle[`${mode}Top`] = readUnit(top);
            }
            if (bottom !== undefined) {
                paddingStyle[`${mode}Bottom`] = readUnit(bottom);
            }
            if (left !== undefined) {
                paddingStyle[`${mode}Left`] = readUnit(left);
            }
            if (right !== undefined) {
                paddingStyle[`${mode}Right`] = readUnit(right);
            }
            return paddingStyle;
        },
        [readUnit],
    );
};
